import Phaser from 'phaser';
import { RenderSystem } from '../systems/RenderSystem';

// ============================================
// Step 7: Entity Framework - Base Entity
// ============================================

export type EntityType = 'player' | 'monster' | 'boss' | 'tower' | 'base' | 'projectile' | 'treeSlot' | 'drop';

export interface EntityConfig {
  id: string;
  type: EntityType;
  x: number;
  y: number;
}

/**
 * Base Entity class that all game entities extend
 */
export abstract class Entity {
  public readonly id: string;
  public readonly type: EntityType;
  protected scene: Phaser.Scene;
  protected container: Phaser.GameObjects.Container;
  protected _x: number;
  protected _y: number;
  protected _active: boolean = true;
  protected _destroyed: boolean = false;

  constructor(scene: Phaser.Scene, config: EntityConfig) {
    this.scene = scene;
    this.id = config.id;
    this.type = config.type;
    this._x = config.x;
    this._y = config.y;

    // Create container for this entity's visuals
    this.container = scene.add.container(config.x, config.y);
    this.container.setData('entityId', this.id);
    this.container.setData('entityType', this.type);
  }

  // Position getters/setters
  get x(): number {
    return this._x;
  }

  set x(value: number) {
    this._x = value;
    this.container.x = value;
  }

  get y(): number {
    return this._y;
  }

  set y(value: number) {
    this._y = value;
    this.container.y = value;
    // Update depth for 2.5D sorting
    this.updateDepth();
  }

  get position(): { x: number; y: number } {
    return { x: this._x, y: this._y };
  }

  setPosition(x: number, y: number): void {
    this._x = x;
    this._y = y;
    this.container.setPosition(x, y);
    this.updateDepth();
  }

  // Active state
  get active(): boolean {
    return this._active;
  }

  set active(value: boolean) {
    this._active = value;
    this.container.setVisible(value);
  }

  get destroyed(): boolean {
    return this._destroyed;
  }

  // Depth sorting - uses bottom of visual so lower-on-screen objects render in front
  protected updateDepth(): void {
    const depth = RenderSystem.calculateUnitDepth(this.getDepthY());
    this.container.setDepth(depth);
  }

  /**
   * Returns the Y value used for depth sorting (bottom of visual by default).
   * Override in subclasses to use the bottom of the entity's image/sprite.
   * Higher Y = further down screen = rendered on top.
   */
  protected getDepthY(): number {
    return this._y;
  }

  /** Public accessor for depth-sort Y (used for occlusion checks, e.g. ghost visibility). */
  getDepthSortY(): number {
    return this.getDepthY();
  }

  // Get the visual container
  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  // Abstract methods that subclasses must implement
  abstract update(delta: number): void;

  // Cleanup
  destroy(): void {
    if (this._destroyed) return;
    this._destroyed = true;
    this._active = false;
    this.container.destroy();
  }

  // Distance to another entity or point
  distanceTo(target: Entity | { x: number; y: number }): number {
    const tx = 'x' in target ? target.x : (target as Entity).x;
    const ty = 'y' in target ? target.y : (target as Entity).y;
    return Phaser.Math.Distance.Between(this._x, this._y, tx, ty);
  }

  distanceSquaredTo(target: Entity | { x: number; y: number }): number {
    const tx = 'x' in target ? target.x : (target as Entity).x;
    const ty = 'y' in target ? target.y : (target as Entity).y;
    return Phaser.Math.Distance.Squared(this._x, this._y, tx, ty);
  }
}
